import { z } from "zod";
import { MAX_AFFIRMATION_LEN, PRESETS } from "./tts.js";

export const Gender = z.enum(["f", "m", "n"]);
export type Gender = z.infer<typeof Gender>;

export const Preset = z.enum(PRESETS);

const isoDate = z.string().datetime({ offset: true });

export const SessionSettings = z
  .object({
    repeats: z.number().int().min(1).max(10).default(3),
    gapSec: z.number().min(0).max(30).default(4),
    loopMinutes: z.number().int().min(0).max(120).default(0), // 0 — один проход
    ambient: z.enum(["none", "drone", "rain"]).default("drone"),
    ambientVolume: z.number().min(0).max(1).default(0.35),
    voiceVolume: z.number().min(0).max(1).default(0.9),
  })
  .strict();
export type SessionSettings = z.infer<typeof SessionSettings>;
export const DEFAULT_SESSION_SETTINGS: SessionSettings = SessionSettings.parse({});

export const AffirmationSet = z.object({
  id: z.string().uuid(),
  title: z.string().trim().min(1).max(120),
  sourceText: z.string().max(4000).nullable().default(null),
  voiceId: z.string().min(1).max(64),
  preset: Preset,
  settings: SessionSettings,
  createdAt: isoDate,
  updatedAt: isoDate,
  deletedAt: isoDate.nullable().default(null),
});
export type AffirmationSet = z.infer<typeof AffirmationSet>;

export const Affirmation = z.object({
  id: z.string().uuid(),
  setId: z.string().uuid(),
  position: z.number().int().min(0).max(1000),
  text: z.string().trim().min(1).max(MAX_AFFIRMATION_LEN),
  createdAt: isoDate,
  updatedAt: isoDate,
  deletedAt: isoDate.nullable().default(null),
});
export type Affirmation = z.infer<typeof Affirmation>;

/** Проработка в тренажёре: ответы, путь по экранам и тексты, которые человек проговаривал */
export const Working = z.object({
  id: z.string().uuid(),
  trainerId: z.string().min(1).max(40),
  title: z.string().trim().min(1).max(120),
  status: z.enum(["active", "done"]),
  stepId: z.string().max(10),
  answers: z
    .record(z.string().max(40), z.string().max(2000))
    .refine((a) => Object.keys(a).length <= 40, "too many answers"),
  /** история экранов для «Назад» */
  trail: z.array(z.string().max(16)).max(200).default([]),
  texts: z.array(z.object({ stepId: z.string().max(10), text: z.string().max(4000) })).max(30).default([]),
  outcome: z.string().max(80).nullable().default(null),
  rating: z.number().int().min(1).max(5).nullable().default(null),
  createdAt: isoDate,
  updatedAt: isoDate,
  finishedAt: isoDate.nullable().default(null),
  deletedAt: isoDate.nullable().default(null),
});
export type Working = z.infer<typeof Working>;

export const SyncRequest = z.object({
  cursor: z.string().regex(/^\d+$/).default("0"),
  changes: z
    .object({
      sets: z.array(AffirmationSet).max(200).default([]),
      affirmations: z.array(Affirmation).max(2000).default([]),
      workings: z.array(Working).max(100).default([]),
    })
    .default({}),
});
export type SyncRequest = z.input<typeof SyncRequest>;

export interface SyncResponse {
  cursor: string;
  sets: AffirmationSet[];
  affirmations: Affirmation[];
  workings: Working[];
  /** id записей, которые сервер отклонил (чужие или невалидные) */
  rejected: string[];
  hasMore: boolean;
}

export const ComposeRequest = z.object({
  text: z.string().trim().min(2).max(4000),
  gender: Gender.default("n"),
  max: z.number().int().min(1).max(20).default(8),
  /** номер подборки: 0 — основная, дальше — «Обновить аффирмации» */
  seed: z.number().int().min(0).max(1_000_000).default(0),
  /** уже показанные фразы — не повторять */
  exclude: z.array(z.string().max(300)).max(60).default([]),
});

export interface ComposeResponse {
  affirmations: string[];
  engine: "rules" | "llm";
}

export const TtsItem = z.object({
  text: z.string().trim().min(1).max(MAX_AFFIRMATION_LEN),
  voiceId: z.string().min(1).max(64),
  preset: Preset,
});
export type TtsItem = z.infer<typeof TtsItem>;

export const TtsBatchRequest = z.object({ items: z.array(TtsItem).min(1).max(50) });

export interface TtsAsset {
  hash: string;
  url: string;
  durationMs: number;
  /** pending — синтез идёт в фоне (личный голос), клиент повторяет запрос */
  status: "ready" | "failed" | "pending";
  error?: string;
}

export interface VoiceProfile {
  id: string;
  voiceId: string;
  durationSec: number | null;
  createdAt: string;
}

export const PracticeIn = z.object({
  id: z.string().uuid(),
  setId: z.string().uuid().nullable(),
  startedAt: isoDate,
  durationSec: z.number().int().min(1).max(6 * 3600),
  completed: z.boolean(),
});
export type PracticeIn = z.infer<typeof PracticeIn>;

export interface Stats {
  totalSessions: number;
  totalMinutes: number;
  streakDays: number;
  lastPracticeAt: string | null;
}

export const ProfilePatch = z.object({
  displayName: z.string().trim().max(60).nullable().optional(),
  gender: Gender.optional(),
  settings: z.record(z.unknown()).optional(),
});

export interface Me {
  id: string;
  displayName: string | null;
  gender: Gender;
  settings: Record<string, unknown>;
  plan: string;
  providers: string[];
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: Me;
}

export interface PublicConfig {
  engineVersion: string;
  audioBaseUrl: string;
  voices: { id: string; name: string; language: string; gender: "f" | "m"; status?: "ready" | "loading" }[];
  presets: string[];
  llm: boolean;
  /** доступна ли озвучка голосом пользователя */
  clone: boolean;
}
